/* eslint-disable sort-keys */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { DepartmentOrderingOptions, DepartmentRegisterOptions } from '../models/department-register-options';
import { DepartmentRegisterListItem, DepartmentRegisterResults } from '../models/department-register-results.model';

import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { AddDepartmentComponent } from '../../add-department/containers/add-department.component';
import { DepartmentsFilterOption } from '../models/department-filter-option';
import { GetDepartment, GetDepartment_departmentsConnection_items } from '../queries/generated/GetDepartment';
import { DepartmentRegisterApiService } from '../services/department-register-api.service';
import * as DepartmentRegisterActions from './department-register.actions';
import {
   createDepartment,
   departmentRegisterAdapterActions,
   setSelectedDepartmentId,
} from './department-register.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './department-register.reducer';
import * as DepartmentRegisterSelectors from './department-register.selectors';
import { selectFilters } from './department-register.selectors';

@Injectable()
export class DepartmentRegisterEffects {
   addDepartment$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createDepartment),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddDepartmentComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadDepartmentList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(departmentRegisterAdapterActions.pageEnter, DepartmentRegisterActions.refreshList),
         withLatestFrom(this.store.pipe(select(selectFilters))),
         switchMap(([, filters]) => {
            return this.loadNewData(filters, null);
         })
      );
   });

   loadDepartmentPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(departmentRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(DepartmentRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, quickFilters]) => {
            return this.loadNewPage(endCursor, filters, quickFilters);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(departmentRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(departmentRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   loadDepartmentNumbers$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(departmentRegisterAdapterActions.pageEnter, DepartmentRegisterActions.refreshList),
         withLatestFrom(this.store.pipe(select(getSelectedAccountId))),
         switchMap(([action, accountId]) => {
            return this.departmentRegisterApiService.getDepartmentNames(accountId).pipe(
               map((result) => {
                  const data = result.data.departmentsConnection.items || [];
                  const depNames = data.map((item) => item.name || '');
                  return DepartmentRegisterActions.loadDepartmentNamesSuccess({
                     departmentNames: depNames,
                  });
               }),
               catchError((error) => {
                  console.error('Error fetching department names:', error);
                  return EMPTY;
               })
            );
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private departmentRegisterApiService: DepartmentRegisterApiService,
      private dialogService: LcsDialogService,
      private twoColumnLayoutService: TwoColumnLayoutService
   ) {}

   private loadNewData(
      filters: DepartmentsFilterOption[],
      quickFilters: DepartmentRegisterOptions | null = null,
      orderingOptions: DepartmentOrderingOptions[] | null = null
   ) {
      return this.departmentRegisterApiService.getDepartment(null, filters, quickFilters, orderingOptions).pipe(
         switchMap((result) => {
            const convertedData = this.convertGetDepartmentResultToLoadSuccessProps(result.data);
            const firstDepartmentId = convertedData.items.length ? convertedData.items[0].departmentId : null;

            this.twoColumnLayoutService.setFirstItemId(firstDepartmentId);

            if (firstDepartmentId !== null) {
               this.twoColumnLayoutService.setShouldShowDetailView(true);
            }

            // if there is 1+ departments, select first one by default
            if (firstDepartmentId !== null && this.twoColumnLayoutService.getSelectedId() === null) {
               this.twoColumnLayoutService.setSelectedId(firstDepartmentId);
               return [
                  departmentRegisterAdapterActions.loadSuccess(convertedData),
                  setSelectedDepartmentId({ departmentId: firstDepartmentId }),
               ];
            } else {
               return [departmentRegisterAdapterActions.loadSuccess(convertedData)];
            }
         }),
         catchError((error) => {
            return of(departmentRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: DepartmentsFilterOption[],
      quickFilters: DepartmentRegisterOptions | null = null,
      orderingOptions: DepartmentOrderingOptions[] | null = null
   ) {
      return this.departmentRegisterApiService.getDepartment(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return departmentRegisterAdapterActions.loadPageSuccess(
               this.convertGetDepartmentResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(departmentRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private convertGetDepartmentResultToLoadSuccessProps(getDepartment: GetDepartment): DepartmentRegisterResults {
      return {
         endCursor: getDepartment.departmentsConnection.pageInfo.endCursor,
         hasNextPage: getDepartment.departmentsConnection.pageInfo.hasNextPage,
         items: getDepartment.departmentsConnection.items.map(this.convertDepartmentRegisterListItem.bind(this)),
         totalCount: getDepartment.departmentsConnection.totalCount,
      };
   }

   private convertDepartmentRegisterListItem(
      getDepartmentItem: GetDepartment_departmentsConnection_items
   ): DepartmentRegisterListItem {
      return {
         departmentId: getDepartmentItem.departmentId || null,
         name: getDepartmentItem.name || null,
         accountId: getDepartmentItem.accountId,
         isActive: getDepartmentItem.isActive,
         createDate: getDepartmentItem.createDate,
         updateDate: getDepartmentItem.updateDate,
         createUser: getDepartmentItem.createUser?.name || null,
         updateUser: getDepartmentItem.updateUser?.name || null,
      };
   }
}
